<!-- eslint-disable vuejs-accessibility/aria-props -->
<template>
  <RevForm
    :initial-values="INITIAL_FORM_VALUES"
    :validate="validate()"
    @submit="handleSubmit"
    @visible-errors-change="handleErrorLog"
  >
    <template #default="{ values, errors }">
      <div class="mb-16">
        <div class="body-1-bold mb-16">
          {{ i18n(translations.userSubtitle) }}
        </div>

        <div class="mb-16">
          <RevCheckbox id="seller_agreement_checkbox" v-model="values.legal">
            <template #label>
              {{ i18n(translations.sellerAgreementCheckBox) }}
            </template>
          </RevCheckbox>
          <p v-if="errors.legal" class="text-action-danger-hi body-2 mt-2">
            {{ errors.legal }}
          </p>
          <p class="mt-8">
            {{ i18n(translations.dontOwnCompany) }}

            <RevLink to="/fr-fr/buyback/home">
              {{ i18n(translations.sellOnBuyBack) }}
            </RevLink>
          </p>
        </div>
        <div class="mb-16">
          <RevInputText
            id="register-readonly-email"
            v-model="values.email"
            data-qa="register-readonly-email"
            disabled
            :icon="IconMail"
            :label="user.email || ''"
          />
        </div>

        <DynamicAddressFieldValidatorsErrorLogger
          :errors
          :form-values="values"
          logger-scope="BO_MERCHANT_REGISTER"
        />
        <AddressFields
          v-model:city="values.city"
          v-model:company="values.company"
          v-model:country="values.country"
          v-model:default-country="values.defaultCountry"
          v-model:firstName="values.firstName"
          v-model:firstNamePronunciation="values.firstNamePronunciation"
          v-model:lastName="values.lastName"
          v-model:lastNamePronunciation="values.lastNamePronunciation"
          v-model:phone="values.phone"
          v-model:postal-code="values.postalCode"
          v-model:state-or-province="values.stateOrProvince"
          v-model:street="values.street"
          :company-field-label-message="i18n(translations.companyLabel)"
          :errors
          :feature-code="FeatureCode.WEB_CHECKOUT_SHIPPING"
          :optional-fields="[]"
        />
        <div class="my-16 space-y-16">
          <RevInputSelect
            id="e-commerce-select"
            v-model="values.selectedRevenue"
            :error="errors.selectedRevenue"
            :label="i18n(translations.eCommerceSelectLabel)"
            :options="revenueOptions"
          />

          <RevInputText
            id="referral-code"
            v-model="values.referralCode"
            :description="i18n(translations.referralCodeInputDescription)"
            :label="i18n(translations.referralCodeInput)"
            type="text"
          />
        </div>

        <p v-if="errorMessage" class="text-action-danger-hi body-2 mt-16">
          {{ errorMessage }}
        </p>

        <RevButton
          id="register_button"
          class="mt-32"
          :disabled="isSubmitting"
          full-width="always"
          :loading="isSubmitting"
          type="submit"
          variant="primary"
        >
          {{ i18n(translations.submitButton) }}
        </RevButton>

        <FormattedMessage
          class="caption my-32 block"
          data-test="legal-link"
          :definition="translations.textTerms"
        >
          <template #termsAndConditions>
            <RevLink
              target="_blank"
              :to="{ name: ROUTE_NAMES.BADOOM.PDF.TERMS_MERCHANT_ONBOARDING }"
            >
              {{ i18n(translations.termsAndConditions) }}
            </RevLink>
          </template>
          <template #email>
            <RevLink :to="`mailto:${translations.emailAddress}`">
              {{ i18n(translations.emailAddress) }}
            </RevLink>
          </template>
        </FormattedMessage>
      </div>
    </template>
  </RevForm>
</template>

<script setup lang="ts">
import { getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { Country, merchantsAPI } from '@backmarket/http-api'
import type { AddressFieldsValues } from '@backmarket/http-api/src/api-specs-shipping/address/address-validation.types'
import { useUserStore } from '@backmarket/nuxt-layer-oauth/useUserStore'
import AddressFields from '@backmarket/nuxt-module-address/AddressFields.vue'
import { AddressFieldsValidators } from '@backmarket/nuxt-module-address/AddressFieldsValidators'
import DynamicAddressFieldValidatorsErrorLogger from '@backmarket/nuxt-module-address/DynamicAddressFieldValidatorsErrorLogger.vue'
import { FeatureCode } from '@backmarket/nuxt-module-address/featureCode'
import { useDynamicAddressFieldValidators } from '@backmarket/nuxt-module-address/useDynamicAddressFieldValidators'
import { useHttpLazyFetch } from '@backmarket/nuxt-module-http/useHttpLazyFetch'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { RevButton } from '@ds/components/Button'
import { RevCheckbox } from '@ds/components/Checkbox'
import {
  RevForm,
  type ValidatorDeclaration,
  makeValidate,
  required,
} from '@ds/components/Form'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevLink } from '@ds/components/Link'
import { IconMail } from '@ds/icons/IconMail'

import { ROUTE_NAMES } from '~/constants/routes'
import type { Error } from '~/utils/apiError'

import {
  type FormValues,
  METADATA,
  type NonNullableFormValues,
  type RegisterBody,
} from './RegisterForm.constants'
import translations from './RegisterForm.translations'

const { user, sellerRegister } = useUserStore()
const tracking = useTracking()
const i18n = useI18n()
const { openErrorToast } = useTheToast()

// @TODO: Need to do this cast because MarketCountryCode and Country are not compatible
const MARKET_COUNTRY_CODE = useMarketplace().market
  .countryCode as unknown as Country

const { getDynamicAddressFieldValidators } = useDynamicAddressFieldValidators()

const { data: metadata, error: metadataError } = await useHttpLazyFetch(
  merchantsAPI.getMerchantRegisterMetadata,
)

if (metadataError.value) {
  openErrorToast()
}

const isKanaNameEnabled = Boolean(metadata.value?.kanaNameEnabled)
const isAddressCountryPrefillEnabled = Boolean(
  metadata.value?.addressCountryPrefillEnabled,
)

const INITIAL_FORM_VALUES: NonNullableFormValues = {
  city: '',
  company: '',
  country: '',
  defaultCountry: isAddressCountryPrefillEnabled
    ? MARKET_COUNTRY_CODE
    : undefined,
  firstName: user.value.firstName ?? '',
  email: '',
  lastName: user.value.lastName ?? '',
  password: '',
  phone: '',
  selectedRevenue: '',
  postalCode: '',
  referralCode: '',
  legal: false,
  stateOrProvince: '',
  street: '',
  ...insertIf(isKanaNameEnabled, {
    firstNamePronunciation: '',
    lastNamePronunciation: '',
  }),
}

const isSubmitting = ref(false)
const errorMessage = ref('')
const revenueOptions = computed(() =>
  (metadata.value?.lastEcommerceRevenueChoices || []).map(
    ({ label, code }: { label: string; code: string }) => ({
      label,
      value: code,
    }),
  ),
)

const fallbackValidators: ValidatorDeclaration<AddressFieldsValues> = {
  ...AddressFieldsValidators(i18n),
  company: { required: required(i18n(translations.fieldIsRequired)) },
}

const addressValidation = await getDynamicAddressFieldValidators({
  featureFlag: 'FF_ENABLE_ADDRESS_FIELD_VALIDATION_REGISTER_FORM',
  initialFormValues: INITIAL_FORM_VALUES,
  fallbackValidators,
  metadata: METADATA,
})

function validate() {
  return makeValidate<NonNullableFormValues>(addressValidation)
}

function sendTrackingEvent(payload: {
  status: string
  values?: FormValues
  error?: unknown
}) {
  return tracking.trackFormSubmit({
    name: 'seller_form_registration',
    payload,
  })
}

async function handleSubmit(values: NonNullableFormValues) {
  isSubmitting.value = true
  errorMessage.value = ''
  try {
    const body: RegisterBody = {
      company: values.company,
      country: values.country,
      address: {
        country: values.country,
        city: values.city,
        postalCode: values.postalCode,
        company: values.company,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phone,
        addressLine: values.street,
      },
    }

    if (values.selectedRevenue) {
      body.lastEcommerceRevenue = values.selectedRevenue
    }
    if (values.referralCode) {
      body.trackingCode = values.referralCode
    }
    if (values.stateOrProvince) {
      body.address.state = values.stateOrProvince
    }
    if (isKanaNameEnabled) {
      body.address.firstNamePronunciation = values.firstNamePronunciation
      body.address.lastNamePronunciation = values.lastNamePronunciation
    }

    await sellerRegister(body as merchantsAPI.PostMerchantRegister.Request)

    sendTrackingEvent({ status: 'success', values })

    /**
     * This is a temporary solution to force the redirection to the bo-seller feedback page
     * while we investigate the root cause of the redirection issue.
     */
    window.location.assign(
      `${window.location.origin}/bo-seller/feedback?registered=OK&formId=OF4Tz5pU`,
    )
  } catch (error) {
    sendTrackingEvent({ status: 'back_error', error })
    errorMessage.value = getApiErrorMessage(error as Error)
  } finally {
    isSubmitting.value = false
  }
}

function handleErrorLog({ errors }: { errors: unknown }) {
  sendTrackingEvent({ status: 'front_error', error: errors })
}
</script>
